// client/src/pages/LeftPane.js
import {
    Avatar,
    Box,
    Button,
    Flex,
    Text,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    useDisclosure,
    useBreakpointValue,
    Textarea,
    IconButton,
    Spinner
} from "@chakra-ui/react";
import { ArrowRight, Copy, Check, ThumbsUp, ThumbsDown } from "lucide-react";
import ChatInput from "../components/ChatInputContainer";
import CreditUsageAlert from "../components/CreditUsageAlert";
import MarkdownRenderer from '../components/MarkdownRenderer';
import TypingDots from '../components/TypingDots';
import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useCreditUsageContext } from '../context/CreditContext';
import { scrollToBottom } from '../utils/chatHelpers';

export default function LeftPane({
    chatId,
    messagesContainerRef,
    turns,
    activeSuggestionTurnId,
    onSendMessage,
    isLoading,
    onScrollMessages,
    isRightPaneOpen,
    typingTurnId,
    typingSuggestionId,
    onToggleSuggestion,
    reports,
    activeReportId,
    onToggleReport,
    typingReportId,
    isLoadingReport,
    isLoadingSuggestions,
    onSubmitFeedback,
    hasMoreTurns,
    isLoadingOlderTurns,
    onLoadOlderTurns,
    feedbackState,
    setFeedbackState,
    onStopGeneration,
    isStopping,
}) {
    const { user } = useAuth();
    const credit = useCreditUsageContext();
    const isMobile = useBreakpointValue({ base: true, md: false });

    // Mobile-only: keep the chat input visible when the on-screen keyboard
    // opens (desktop keeps its exact current focus behavior).
    const handleInputFocus = () => {
        if (isMobile) {
            scrollToBottom(messagesContainerRef);
            setTimeout(() => {
                scrollToBottom(messagesContainerRef);
            }, 250);
        }
    };

    // Track which message was copied (by turn.id and type: 'user' or 'bot')
    const [copiedMessage, setCopiedMessage] = useState(null);
    const [feedbackData, setFeedbackData] = useState({
        messageId: null,
        feedbackType: null,
        comment: ''
    });
    const { isOpen, onOpen, onClose } = useDisclosure();

    const handleCopyMessage = (text, messageId, messageType) => {
        navigator.clipboard.writeText(text);

        // Set copied state for this specific message
        setCopiedMessage({ id: messageId, type: messageType });

        // Reset after 2 seconds
        setTimeout(() => {
            setCopiedMessage(null);
        }, 2000);
    };

    const handleFeedbackClick = (messageId, feedbackType) => {
        const currentFeedback = feedbackState[messageId];
        const currentType = currentFeedback?.type;

        // Toggle behavior:
        // If clicking the same icon that's already active, we'll open dialog to remove
        // If clicking different icon, switch to that feedback
        // If no feedback yet, set new feedback

        if (currentType === feedbackType) {
            // Same icon clicked - prepare to remove feedback
            setFeedbackData({
                messageId,
                feedbackType: null, // Will remove feedback
                comment: currentFeedback?.comment || '',
                isRemoving: true
            });
        } else {
            // Different icon or new feedback
            setFeedbackData({
                messageId,
                feedbackType,
                comment: currentFeedback?.comment || '', // Preserve existing comment if updating
                isRemoving: false
            });
        }
        onOpen();
    };

    const handleFeedbackSubmit = () => {
        const { messageId, feedbackType, comment, isRemoving } = feedbackData;

        if (isRemoving) {
            // Remove feedback
            setFeedbackState(prev => ({
                ...prev,
                [messageId]: { type: null, comment: '' }
            }));

            // TODO: migrate to aws, Call the callback to update Firestore
            if (onSubmitFeedback) {
                onSubmitFeedback(messageId, null, '');
            }
        } else {
            // Add or update feedback
            setFeedbackState(prev => ({
                ...prev,
                [messageId]: { type: feedbackType, comment: comment || '' }
            }));

            // TODO: migrate to aws, Call the callback to update Firestore
            if (onSubmitFeedback) {
                onSubmitFeedback(messageId, feedbackType, comment || '');
            }
        }

        onClose();
        // Reset feedback data
        setFeedbackData({
            messageId: null,
            feedbackType: null,
            comment: '',
            isRemoving: false
        });
    };

    const handleFeedbackCancel = () => {
        onClose();
        setFeedbackData({
            messageId: null,
            feedbackType: null,
            comment: '',
            isRemoving: false
        });
    };

    // ── Temporary pending-response placeholder (presentation-only) ──────────
    // Derived solely from existing chat-scoped props; nothing here mutates
    // `turns` or any session state. The real assistant "typing" indicator only
    // renders inside a visible assistant bubble whose id === typingTurnId and
    // whose content is non-empty (see the empty-content skip in the map above).
    // This placeholder is shown precisely while that condition is FALSE but a
    // send is still in flight for this chat — i.e. during the backend
    // processing delay before the first streamed token — so the two indicators
    // are mutually exclusive by construction and can never appear together.
    //
    // Right-pane streaming (typingSuggestionId / typingReportId) suppresses
    // ONLY this temporary placeholder — never the real left streaming
    // indicator. isLoadingSuggestions additionally covers the post-stream
    // extraction window (after typingSuggestionId clears but while the
    // analysis flow is still settling), so the pending dots cannot briefly
    // reappear once the right-pane flow has begun.
    const realIndicatorVisible =
        typingTurnId != null &&
        turns.some(
            (turn) =>
                turn.role === 'assistant' &&
                turn.id === typingTurnId &&
                turn.content &&
                turn.content.trim() !== ''
        );
    const rightPaneActive =
        typingSuggestionId != null ||
        typingReportId != null ||
        Boolean(isLoadingSuggestions);
    const showPendingTyping =
        Boolean(isLoading) && typingTurnId != null && !realIndicatorVisible && !rightPaneActive;

    const isReportGenerating = Boolean(isLoadingReport || typingReportId);

    return (
        <Flex flexDir="column" h="100%" w="100%" bg="white"
            border={{ base: "none", md: "1px solid #ECECEC" }}
            boxSizing="border-box"
            borderRadius={{ base: 0, md: "12px" }}
            ml={0}
            overflowY="auto"
            overflowX="hidden"
            ref={messagesContainerRef}
            onScroll={onScrollMessages}
            css={{
                scrollbarWidth: 'thin',
                scrollbarColor: '#D0D0D0 transparent',
                '&::-webkit-scrollbar': {
                    width: '6px',
                },
                '&::-webkit-scrollbar-track': {
                    background: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                    background: '#D0D0D0',
                    borderRadius: '3px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                    background: '#B0B0B0',
                },
                scrollBehavior: 'auto',
            }}
        >
            <Box flex="1" display="flex"
                flexDir="column"
                px={isRightPaneOpen ? '16px' : { base: '10px', md: '0' }}
            >
                <Box
                    maxW={isRightPaneOpen ? '100%' : '850px'}
                    w="100%"
                    mx={isRightPaneOpen ? '0' : 'auto'}
                    pt="16px"
                    pb={{ base: "24px", md: "16px" }}
                >
                    {hasMoreTurns && (
                        <Box display="flex" justifyContent="center" py={3}>
                            <Button
                                size="sm"
                                variant="ghost"
                                color="#9E9E9E"
                                borderRadius="12px"
                                isLoading={isLoadingOlderTurns}
                                onClick={onLoadOlderTurns}
                                _hover={{ bg: "#F7F7F7", color: "#4F4F4F" }}
                                border="1px solid #ECECEC"
                            >
                                Load older messages
                            </Button>
                        </Box>
                    )}
                    {turns.map((turn) => {
                        const isTyping = typingTurnId === turn.id;
                        const isUserTurn = turn.role === 'user';
                        const isAssistantTurn = turn.role === 'assistant';
                        const isAnalysisTurn = turn.role === 'analysis';
                        const isSuggestionActive = activeSuggestionTurnId === turn.id && !activeReportId;
                        const turnReports = isAnalysisTurn ? (reports[turn.id] || []) : [];

                        // Skip assistant turns with empty content
                        if (isAssistantTurn && (!turn.content || turn.content.trim() === '')) return null;

                        // Get feedback state for this bot message
                        const botMessageFeedback = isAssistantTurn ? feedbackState[turn.id] : null;
                        const isThumbsUpActive = botMessageFeedback?.type === 'positive';
                        const isThumbsDownActive = botMessageFeedback?.type === 'negative';

                        return (
                            <Box key={turn.id}
                            // mb="32px"
                            >
                                {/* USER MESSAGE */}
                                {isUserTurn && (
                                    <Flex
                                        role="group"
                                        justifyContent="flex-end"
                                        alignItems="flex-start"
                                        mb="8px"
                                        flexDirection="column"
                                    >
                                        <Flex justifyContent="flex-end" gap="8px" alignItems="flex-start" w="100%">
                                            <Box
                                                display="inline-block"
                                                bg="#F3F3F3"
                                                color="#242424"
                                                px="16px"
                                                py="8px"
                                                borderRadius="16px"
                                                borderTopRightRadius="0"
                                                fontSize="14px"
                                                maxW={{ base: "88%", md: "70%" }}
                                                wordBreak="break-word"
                                            >
                                                {/* Show current files if they exist */}
                                                {turn.files && turn.files.length > 0 && (
                                                    <Flex
                                                        flexWrap="wrap"
                                                        gap="4px"
                                                        mb={isUserTurn ? "8px" : "0"}
                                                    >
                                                        {turn.files.map((file, index) => (
                                                            <Box
                                                                key={index}
                                                                bg="#ffffff"
                                                                borderRadius="100px"
                                                                border="1px solid #DADADA"
                                                                px="10px"
                                                                py="6px"
                                                                fontSize="12px"
                                                                fontWeight="semibold"
                                                                display="flex"
                                                                alignItems="center"
                                                                gap="8px"
                                                                color="#242424"
                                                                cursor={file.uploading ? 'default' : 'pointer'}
                                                                opacity={file.uploading ? 0.6 : 1}
                                                                _hover={!file.uploading ? { bg: '#E8E8E8' } : {}}
                                                                onClick={async () => {
                                                                    if (file.uploading || file.uploadError) return;

                                                                    if (file.presigned_url) {
                                                                        window.open(file.presigned_url, '_blank');
                                                                        return;
                                                                    }

                                                                    if (file.id) {
                                                                        try {
                                                                            const { fetchWithAuth } = await import('../context/AuthContext');
                                                                            const res = await fetchWithAuth(
                                                                                `${process.env.REACT_APP_WEB_SERVER_URL || 'http://localhost:8080'}/files/preview/${file.id}`
                                                                            );
                                                                            const data = await res.json();
                                                                            if (data.url) window.open(data.url, '_blank');
                                                                        } catch (err) {
                                                                            console.error('Failed to get download URL:', err);
                                                                        }
                                                                    }
                                                                }}
                                                            >
                                                                {file.uploading && <Spinner size="xs" color="gray.500" />}
                                                                {file.uploadError && <Text color="red.400" fontSize="11px">✕</Text>}
                                                                <Text noOfLines={1} maxW="180px">
                                                                    {file.filename || file.name}
                                                                </Text>
                                                            </Box>
                                                        ))}
                                                    </Flex>
                                                )}

                                                {/* Show text with markdown support */}
                                                {isUserTurn && turn.content && (
                                                    <Box mt={turn.files?.length > 0 ? "8px" : "0"}>
                                                        <MarkdownRenderer content={turn.content} />
                                                    </Box>
                                                )}

                                                {isUserTurn && !turn.content && (!turn.files || turn.files.length === 0) && (
                                                    <Text>[Empty message]</Text>
                                                )}
                                            </Box>

                                            <Avatar
                                                size="sm"
                                                name={user?.fullName || user?.email || 'User'}
                                                src={user?.photoURL || undefined}
                                                bg="#F3F3F3"
                                                color="#7A7A7A"
                                                flexShrink={0}
                                            />
                                        </Flex>

                                        {/* Copy Button - Always takes space (24px height), visible on hover */}
                                        <Flex justifyContent="flex-end" w="100%" pr="48px" pt={1}>
                                            <Box h="24px" display="flex" alignItems="center">
                                                <Button
                                                    opacity={{ base: 0.85, md: 0 }}
                                                    _groupHover={{ opacity: 1 }}
                                                    transition="opacity 0.2s"
                                                    onClick={() => handleCopyMessage(
                                                        turn.content || turn.files?.map(f => f.name).join(', ') || '',
                                                        turn.id,
                                                        'user'
                                                    )}
                                                    minW="unset"
                                                    h="24px"
                                                    w="24px"
                                                    p="0"
                                                    _hover={{ bg: "#F3F3F3" }}
                                                    bg="transparent"
                                                >
                                                    {copiedMessage?.id === turn.id && copiedMessage?.type === 'user' ? (
                                                        <Check size={16} />
                                                    ) : (
                                                        <Copy size={16} />
                                                    )}
                                                </Button>
                                            </Box>
                                        </Flex>
                                    </Flex>
                                )}

                                {/* BOT MESSAGE */}
                                {isAssistantTurn && (
                                    <Flex justifyContent="flex-start" mb="12px" role="group" gap="8px" alignItems="flex-start">
                                        {/* Bot Avatar - replace src with your bot avatar image */}
                                        {/* Bot Avatar */}
                                        <Box
                                            w="30px"
                                            h="30px"
                                            borderRadius="full"
                                            flexShrink={0}
                                            mt="4px"
                                            bg="white"
                                            border="1px solid"
                                            borderColor="gray.100"
                                            style={{
                                                backgroundImage: 'url(/logo.svg)',
                                                backgroundSize: '79%',
                                                backgroundRepeat: 'no-repeat',
                                                backgroundPosition: 'center',
                                            }}
                                        />
                                        <Flex direction="column" alignItems="flex-start" w="100%">
                                            <Box
                                                display="inline-block"
                                                bg="white"
                                                px="16px"
                                                py="8px"
                                                borderRadius="16px"
                                                fontSize="14px"
                                                border="1px solid"
                                                borderColor="gray.200"
                                                maxW={{ base: "88%", md: "75%" }}
                                                // whiteSpace="pre-wrap"
                                                wordBreak="break-word"
                                            >
                                                <MarkdownRenderer content={turn.content} />
                                                {/* TYPING INDICATOR (inside the real streaming bubble) */}
                                                {isTyping && <TypingDots />}
                                            </Box>
                                            {/* Copy Button - Shows on hover */}
                                            {isAssistantTurn && turn.content && (
                                                <Flex justifyContent="flex-start" w="100%" pl="10px" pt={1}>

                                                    <IconButton
                                                        onClick={() => handleFeedbackClick(turn.id, 'positive')}
                                                        minW="unset"
                                                        h="24px"
                                                        w="24px"
                                                        p={0}
                                                        _hover={{ bg: '#F3F3F3' }}
                                                        bg={isThumbsUpActive ? '#E8F5E9' : 'transparent'}
                                                        aria-label="Thumbs up"
                                                        icon={<ThumbsUp size={16} color={isThumbsUpActive ? '#4CAF50' : '#666'} fill={'none'} />}
                                                    />

                                                    {/* Thumbs Down Button */}
                                                    <IconButton
                                                        onClick={() => handleFeedbackClick(turn.id, 'negative')}
                                                        minW="unset"
                                                        h="24px"
                                                        w="24px"
                                                        p={0}
                                                        _hover={{ bg: '#F3F3F3' }}
                                                        bg={isThumbsDownActive ? '#FFEBEE' : 'transparent'}
                                                        aria-label="Thumbs down"
                                                        icon={<ThumbsDown size={16} color={isThumbsDownActive ? '#F44336' : '#666'} fill={'none'} />}
                                                    />

                                                    <IconButton
                                                        onClick={() => handleCopyMessage(turn.content, turn.id, 'bot')}
                                                        minW="unset"
                                                        h="24px"
                                                        w="24px"
                                                        p={0}
                                                        _hover={{ bg: '#F3F3F3' }}
                                                        bg="transparent"
                                                        aria-label="Copy message"
                                                        icon={
                                                            copiedMessage?.id === turn.id && copiedMessage?.type === 'bot' ? (
                                                                <Check size={16} />
                                                            ) : (
                                                                <Copy size={16} />
                                                            )
                                                        }
                                                    />

                                                </Flex>
                                            )}
                                        </Flex>
                                    </Flex>
                                )}
                                {/* SHOW SUGGESTIONS BUTTON */}
                                {isAnalysisTurn && (
                                    <Button
                                        onClick={() => onToggleSuggestion(turn.id)}
                                        w="100%"
                                        // mt="12px"
                                        mb="12px"
                                        h="75px"
                                        borderRadius="12px"
                                        bg={isSuggestionActive
                                            ? 'linear-gradient(180deg, rgba(255, 200, 235, 1) 0%, rgba(200, 235, 255, 1) 100%)'
                                            : 'linear-gradient(180deg, rgba(255, 230, 250, 0.8) 0%, rgba(230, 250, 255, 0.8) 100%)'
                                        }
                                        border="1px solid"
                                        borderColor={isSuggestionActive ? "#FF9CEE" : "#DADADA"}
                                        color="#242424"
                                        fontSize="14px"
                                        fontWeight="medium"
                                        textAlign="left"
                                        display="flex"
                                        flexDirection="column"
                                        alignItems="flex-start"
                                        position="relative"
                                        pl="16px"
                                        pr={{ base: "44px", md: "16px" }}
                                        boxShadow={isSuggestionActive ? '0 4px 12px rgba(207, 0, 138, 0.2)' : 'none'}
                                        _hover={{
                                            bg: 'linear-gradient(180deg, rgba(255, 220, 245, 1) 0%, rgba(220, 245, 255, 1) 100%)',
                                            transform: 'translateY(-1px)',
                                            boxShadow: '0 4px 12px rgba(207, 0, 138, 0.15)'
                                        }}
                                        _active={{
                                            transform: 'translateY(0)',
                                            boxShadow: '0 2px 6px rgba(207, 0, 138, 0.1)'
                                        }}
                                        transition="all 0.2s ease"
                                    >
                                        {/* <Flex alignItems="center" gap="8px" mb="4px">
                                                <Box
                                                    width="16px"
                                                    height="16px"
                                                    backgroundImage="url('/icon_btn_pink.svg')"
                                                    backgroundSize="contain"
                                                    backgroundRepeat="no-repeat"
                                                    backgroundPosition="center"
                                                /> */}
                                        <Text fontSize="14px" fontWeight="600" color="#242424">
                                            {typingSuggestionId === turn.id ? 'Generating suggestions...' : 'Suggestions generated'}
                                        </Text>
                                        {/* </Flex> */}
                                        <Box position="absolute" right="16px" top="50%" transform="translateY(-50%)">
                                            <ArrowRight size={20} color="#CF008A" />
                                        </Box>
                                    </Button>
                                )}

                                {isAnalysisTurn && (
                                    <>
                                        {/* Report Buttons */}
                                        {turnReports.map((report) => {
                                            const isReportActive = activeReportId?.turnId === turn.id && activeReportId?.version === report.version;
                                            // Check if THIS specific report is being generated/streamed
                                            const isThisReportGenerating =
                                                (typingReportId?.turnId === turn.id && typingReportId?.version === report.version) ||
                                                (isLoadingReport && activeReportId?.turnId === turn.id && activeReportId?.version === report.version);

                                            // Check if report is complete
                                            const isReportComplete = report.isComplete === true;

                                            // Determine button text
                                            const reportButtonText = isThisReportGenerating
                                                ? 'Generating report...'
                                                : (report.status === 'rejected'
                                                    ? 'Report failed'
                                                    : (isReportComplete ? 'Report generated' : 'Report pending'));

                                            return (
                                                <Button
                                                    key={report.version}
                                                    onClick={() => onToggleReport(turn.id, report.version)}
                                                    w="100%"
                                                    mt="12px"
                                                    mb="12px"
                                                    h="75px"
                                                    borderRadius="12px"
                                                    // ✅ Conditional background based on active state
                                                    bg={isReportActive
                                                        ? 'linear-gradient(180deg, rgba(255, 200, 235, 1) 0%, rgba(200, 235, 255, 1) 100%)'
                                                        : 'linear-gradient(180deg, rgba(255, 230, 250, 0.8) 0%, rgba(230, 250, 255, 0.8) 100%)'
                                                    }
                                                    border="1px solid"
                                                    // ✅ Conditional border based on active state
                                                    borderColor={isReportActive ? "#FF9CEE" : "#DADADA"}
                                                    color="#242424"
                                                    fontSize="14px"
                                                    fontWeight="medium"
                                                    textAlign="left"
                                                    display="flex"
                                                    flexDirection="column"
                                                    alignItems="flex-start"
                                                    position="relative"
                                                    pl="16px"
                                                    pr={{ base: "44px", md: "16px" }}
                                                    // ✅ Optional: Add box shadow when active
                                                    boxShadow={isReportActive ? '0 4px 12px rgba(207, 0, 138, 0.2)' : 'none'}
                                                    _hover={{
                                                        bg: 'linear-gradient(180deg, rgba(255, 220, 245, 1) 0%, rgba(220, 245, 255, 1) 100%)',
                                                        transform: 'translateY(-1px)',
                                                        boxShadow: '0 4px 12px rgba(207, 0, 138, 0.15)'
                                                    }}
                                                    _active={{
                                                        transform: 'translateY(0)',
                                                        boxShadow: '0 2px 6px rgba(207, 0, 138, 0.1)'
                                                    }}
                                                    transition="all 0.2s ease"
                                                >
                                                    <Flex alignItems="center" gap="8px" mb="4px">

                                                        <Text fontSize="14px" fontWeight="600" color="#242424">
                                                            {/* Report generated */}
                                                            {reportButtonText}
                                                        </Text>
                                                    </Flex>

                                                    <Text fontSize="13px" fontWeight="500" color="#CF008A">
                                                        V{report.version}
                                                    </Text>

                                                    {/* Arrow Icon */}
                                                    <Box
                                                        position="absolute"
                                                        right="20px"
                                                        top="50%"
                                                        transform="translateY(-50%)"
                                                    >
                                                        <ArrowRight size={20} color="#CF008A" />
                                                    </Box>
                                                </Button>
                                            );
                                        })}
                                    </>
                                )}


                            </Box>
                        );
                    })}

                    {/* TEMPORARY PENDING-RESPONSE PLACEHOLDER (presentation-only).
                        Rendered independently of `turns` — it is never inserted into the
                        turns array and is never persisted. Shown only while a send is in
                        flight for THIS chat and the real assistant bubble has not yet
                        produced visible content; it disappears automatically the moment
                        the real streaming bubble takes over (see showPendingTyping). */}
                    {showPendingTyping && (
                        <Flex justifyContent="flex-start" mb="12px" gap="8px" alignItems="flex-start">
                            {/* Bot Avatar — mirrors the real assistant bubble styling */}
                            <Box
                                w="30px"
                                h="30px"
                                borderRadius="full"
                                flexShrink={0}
                                mt="4px"
                                bg="white"
                                border="1px solid"
                                borderColor="gray.100"
                                style={{
                                    backgroundImage: 'url(/logo.svg)',
                                    backgroundSize: '79%',
                                    backgroundRepeat: 'no-repeat',
                                    backgroundPosition: 'center',
                                }}
                            />
                            <Flex direction="column" alignItems="flex-start" w="100%">
                                <Box
                                    display="inline-block"
                                    bg="white"
                                    px="16px"
                                    py="8px"
                                    borderRadius="16px"
                                    fontSize="14px"
                                    border="1px solid"
                                    borderColor="gray.200"
                                    maxW={{ base: "88%", md: "75%" }}
                                    wordBreak="break-word"
                                >
                                    <TypingDots />
                                </Box>
                            </Flex>
                        </Flex>
                    )}
                </Box>
            </Box>

            <Box
                flexShrink={0}
                position="sticky"
                bottom="0"
                zIndex={1}
                bg="#fff"
                pt="12px"
                pb="12px"
                px={isRightPaneOpen ? '16px' : { base: '10px', md: '0' }}
                borderRadius="16px"
            >
                <Box maxW={isRightPaneOpen ? '100%' : '850px'} w="100%" mx={isRightPaneOpen ? '0' : 'auto'}>
                    <CreditUsageAlert />
                    <ChatInput chatId={chatId} isLoading={isLoading} isReportLoading={isReportGenerating} onSend={onSendMessage} onInputFocus={isMobile ? handleInputFocus : undefined} mode="chat" disabled={typingTurnId !== null || typingSuggestionId !== null || isReportGenerating || credit.isCreditUnavailable} onStop={onStopGeneration} isStopping={isStopping} />
                </Box>
            </Box>

            {/* Feedback Dialog Modal */}

            <Modal isOpen={isOpen} onClose={handleFeedbackCancel} isCentered>
                <ModalOverlay backdropFilter="blur(4px)" />
                <ModalContent borderRadius="16px" p={2}>

                    <ModalHeader fontSize="16px" fontWeight="700" color="#1A1A1A" pb={2}>
                        {feedbackData.isRemoving ? 'Remove Feedback' : 'Provide Feedback'}
                    </ModalHeader>

                    <ModalBody>
                        {feedbackData.isRemoving ? (
                            <Text fontSize="14px" color="#6D6D6D">
                                Are you sure you want to remove your feedback?
                            </Text>
                        ) : (
                            <>
                                <Text fontSize="12px" fontWeight="600" color="#9E9E9E"
                                    textTransform="uppercase" letterSpacing="0.5px" mb={2}>
                                    Selected feedback
                                </Text>
                                <Flex
                                    align="center"
                                    gap={2}
                                    mb={4}
                                    bg={feedbackData.feedbackType === 'positive' ? '#F6FFF6' : '#FFF5F5'}
                                    border={`1px solid ${feedbackData.feedbackType === 'positive' ? '#C3E6C3' : '#FCCACA'}`}
                                    borderRadius="10px"
                                    px={3}
                                    py={2}
                                >
                                    {feedbackData.feedbackType === 'positive' ? (
                                        <>
                                            <ThumbsUp size={18} color="#4CAF50" />
                                            <Text fontSize="13px" fontWeight="600" color="#4CAF50">Thumbs Up</Text>
                                        </>
                                    ) : (
                                        <>
                                            <ThumbsDown size={18} color="#E53E3E" />
                                            <Text fontSize="13px" fontWeight="600" color="#E53E3E">Thumbs Down</Text>
                                        </>
                                    )}
                                </Flex>
                            </>
                        )}

                        {!feedbackData.isRemoving && (
                            <>
                                <Text fontSize="13px" color="#6D6D6D" mb={2}>
                                    Additional comments (optional)
                                </Text>
                                <Textarea
                                    value={feedbackData.comment}
                                    onChange={(e) => setFeedbackData({ ...feedbackData, comment: e.target.value })}
                                    placeholder="Share your thoughts..."
                                    rows={4}
                                    borderRadius="12px"
                                    borderColor="#E0E0E0"
                                    fontSize="13px"
                                    resize="none"
                                    _focus={{ borderColor: '#CF008A', boxShadow: '0 0 0 1px #CF008A' }}
                                    _placeholder={{ color: '#ABABAB' }}
                                />
                            </>
                        )}
                    </ModalBody>

                    <ModalFooter gap={2} pt={3}>
                        <Button
                            variant="ghost"
                            onClick={handleFeedbackCancel}
                            borderRadius="10px"
                            fontSize="14px"
                            color="#6D6D6D"
                            _hover={{ bg: '#F5F5F5' }}
                        >
                            Cancel
                        </Button>
                        <Button
                            bg={feedbackData.isRemoving ? '#E53E3E' : '#CF008A'}
                            color="white"
                            onClick={handleFeedbackSubmit}
                            borderRadius="10px"
                            fontSize="14px"
                            fontWeight="600"
                            _hover={{ bg: feedbackData.isRemoving ? '#C53030' : '#D933A1' }}
                        >
                            {feedbackData.isRemoving ? 'Remove' : 'Submit'}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

        </Flex>
    );
}
